import { state } from '../core/state.js';
import { socket } from '../core/socket.js';
import { popToast } from '../utils/alerts.js';
import { t } from '../core/i18n.js';

export function initVoiceRecorder() {
    const btnVocal = document.getElementById('btn-vocal');
    const recEl    = document.getElementById('voice-recorder');
    if (!btnVocal || !recEl) return;

    const lockTrack = document.getElementById('vr-lock-track');
    const timerEl   = document.getElementById('vr-timer');
    const hintEl    = document.getElementById('vr-hint');
    const pauseBtn  = document.getElementById('vr-pause');
    const sendBtn   = document.getElementById('vr-send');
    const trashBtn  = document.getElementById('vr-trash');

    const LOCK_THRESHOLD   = 60;   // px vers le haut → verrouillage
    const CANCEL_THRESHOLD = 90;   // px vers la gauche → annulation
    const MIN_MS           = 700;  // appui trop court → annulé
    const MAX_MS           = 5 * 60 * 1000;
    const PAUSE_SUPPORTED  = !!(window.MediaRecorder && typeof MediaRecorder.prototype.pause === 'function');

    // Pas de support d'enregistrement → on masque le bouton purement et simplement.
    if (!window.MediaRecorder || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        btnVocal.style.display = 'none';
        return;
    }

    let stream = null, recorder = null, chunks = [], mimeType = '';
    let startTime = 0, pausedAt = 0, accPausedMs = 0, timerInt = null, maxTimeout = null;
    let vrState = 'idle';            // idle | recording | locked (renommé state en vrState pour éviter le conflit avec core/state)
    let ptrId = null, startX = 0, startY = 0, cancelled = false;
    // #5 : indicateur d'enregistrement vocal pour les autres.
    let _recHb = null, _recCtx = null;

    function pickMime() {
        const cands = [
            'audio/webm;codecs=opus', 'audio/webm',
            'audio/mp4', 'audio/mp4;codecs=mp4a.40.2',
            'audio/ogg;codecs=opus'
        ];
        for (const c of cands) { if (MediaRecorder.isTypeSupported(c)) return c; }
        return '';
    }
    function extFor(m) {
        if (m.includes('webm')) return 'webm';
        if (m.includes('mp4') || m.includes('m4a') || m.includes('aac')) return 'm4a';
        if (m.includes('ogg'))  return 'ogg';
        if (m.includes('mpeg')) return 'mp3';
        return 'webm';
    }
    function fmtDur(sec) {
        sec = Math.max(0, Math.floor(sec));
        const m = Math.floor(sec / 60), s = sec % 60;
        return m + ':' + String(s).padStart(2, '0');
    }
    function elapsedMs() {
        const extra = (recorder && recorder.state === 'paused') ? (Date.now() - pausedAt) : 0;
        return Date.now() - startTime - accPausedMs - extra;
    }
    function abToB64(buf) {
        const b = new Uint8Array(buf); let s = '';
        for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
        return btoa(s);
    }

    function resetUI() {
        recEl.style.display = 'none';
        recEl.classList.remove('locked', 'paused', 'no-pause');
        lockTrack.classList.remove('visible', 'armed');
        btnVocal.classList.remove('recording');
        btnVocal.style.visibility = ''; // ré-affiche le micro (cf. lock())
        hintEl.textContent = t('voicemsg.slide_cancel');
        pauseBtn.innerHTML = '<i class="fas fa-pause"></i>';
        timerEl.textContent = '0:00';
    }
    function teardown() {
        clearInterval(timerInt); timerInt = null;
        clearTimeout(maxTimeout); maxTimeout = null;
        // #5 : couper le heartbeat + prévenir l'arrêt
        if (_recHb || _recCtx) {
            clearInterval(_recHb); _recHb = null;
            if (_recCtx) socket.emit('stop-recording', _recCtx);
            _recCtx = null;
        }
        if (stream) { stream.getTracks().forEach(tr => tr.stop()); stream = null; }
        recorder = null; chunks = []; vrState = 'idle'; ptrId = null;
        resetUI();
    }

    async function startRecording(e) {
        if (vrState !== 'idle') return;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (err) {
            popToast(t('voicemsg.mic_denied'), 'error');
            teardown();
            return;
        }
        mimeType = pickMime();
        try {
            recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        } catch (err) {
            popToast(t('voicemsg.unsupported'), 'error');
            teardown();
            return;
        }
        if (!mimeType) mimeType = recorder.mimeType || 'audio/webm';
        chunks = [];
        cancelled = false;
        accPausedMs = 0;
        recorder.ondataavailable = ev => { if (ev.data && ev.data.size > 0) chunks.push(ev.data); };
        recorder.onstop = () => {
            const wasCancelled = cancelled;
            const dur = elapsedMs() / 1000;
            const blob = new Blob(chunks, { type: mimeType });
            teardown();
            if (wasCancelled || blob.size === 0) return;
            finaliser(blob, dur);
        };
        recorder.start();
        vrState = 'recording';
        startTime = Date.now();

        _recCtx = { destinataire: state.destinataireID, salonId: state.salonActuelID };
        socket.emit('recording', _recCtx);
        clearInterval(_recHb);
        _recHb = setInterval(() => socket.emit('recording', _recCtx), 2500);

        btnVocal.classList.add('recording');
        recEl.style.display = 'flex';
        if (!PAUSE_SUPPORTED) recEl.classList.add('no-pause');
        lockTrack.classList.add('visible');
        timerEl.textContent = '0:00';
        timerInt = setInterval(() => { timerEl.textContent = fmtDur(elapsedMs() / 1000); }, 250);
        maxTimeout = setTimeout(() => {
            if (vrState === 'recording' || vrState === 'locked') {
                popToast(t('voicemsg.max_reached'), 'stream');
                stopAndSend();
            }
        }, MAX_MS);

        ptrId = e.pointerId;
        startX = e.clientX; startY = e.clientY;
        try { btnVocal.setPointerCapture(ptrId); } catch (_) {}
    }

    function lock() {
        if (vrState !== 'recording') return;
        vrState = 'locked';
        recEl.classList.add('locked');
        lockTrack.classList.remove('visible', 'armed');
        hintEl.textContent = t('voicemsg.locked');
        btnVocal.style.visibility = 'hidden';
        if (ptrId !== null) { try { btnVocal.releasePointerCapture(ptrId); } catch (_) {} }
    }
    function cancelRecording() {
        if (vrState === 'idle') return;
        cancelled = true;
        popToast(t('voicemsg.cancelled'), 'stream');
        try { if (recorder && recorder.state !== 'inactive') recorder.stop(); else teardown(); }
        catch (_) { teardown(); }
    }
    function stopAndSend() {
        if (vrState === 'idle') return;
        if (elapsedMs() < MIN_MS) {
            cancelled = true;
            popToast(t('voicemsg.too_short'), 'stream');
        }
        try { if (recorder && recorder.state !== 'inactive') recorder.stop(); else teardown(); }
        catch (_) { teardown(); }
    }
    function togglePause() {
        if (!recorder) return;
        if (recorder.state === 'recording') {
            try { recorder.pause(); } catch (_) {}
            if (recorder.state !== 'paused') { recEl.classList.add('no-pause'); return; }
            pausedAt = Date.now();
            recEl.classList.add('paused');
            hintEl.textContent = t('voicemsg.paused');
            pauseBtn.innerHTML = '<i class="fas fa-play"></i>';
        } else if (recorder.state === 'paused') {
            try { recorder.resume(); } catch (_) {}
            accPausedMs += Date.now() - pausedAt;
            recEl.classList.remove('paused');
            hintEl.textContent = t('voicemsg.locked');
            pauseBtn.innerHTML = '<i class="fas fa-pause"></i>';
        }
    }

    // --- Gestes pointeur (souris + tactile unifiés) ---
    btnVocal.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        startRecording(e);
    });
    btnVocal.addEventListener('pointermove', (e) => {
        if (vrState !== 'recording' || e.pointerId !== ptrId) return;
        const dy = startY - e.clientY;   // >0 : vers le haut
        const dx = startX - e.clientX;   // >0 : vers la gauche
        if (dx > CANCEL_THRESHOLD && dx > dy) { cancelRecording(); return; }
        if (dy > LOCK_THRESHOLD) { lock(); return; }
        lockTrack.classList.toggle('armed', dy > LOCK_THRESHOLD * 0.5);
    });
    btnVocal.addEventListener('pointerup', (e) => {
        if (e.pointerId !== ptrId) return;
        if (vrState === 'recording') stopAndSend();
    });
    btnVocal.addEventListener('pointercancel', (e) => {
        if (e.pointerId !== ptrId) return;
        if (vrState === 'recording') cancelRecording();
    });

    pauseBtn.addEventListener('click', togglePause);
    trashBtn.addEventListener('click', cancelRecording);
    sendBtn.addEventListener('click', stopAndSend);

    // --- Routage de l'envoi : MP E2EE d'abord, sinon canal public ---
    async function finaliser(blob, durSec) {
        const enMP = !!state.destinataireID;
        if (enMP && window.ChaosMPCrypto && await window.ChaosKeystore.getMeta('device_id')) {
            try {
                const pairE2EE = await window.ChaosMPCrypto.pairEstE2EE(state.destinataireID, socket);
                if (pairE2EE) {
                    await envoyerVocalE2EE(blob, durSec);
                    return;
                }
            } catch (err) {
                console.warn('[Vocal] Vérif E2EE échouée, fallback clair :', err.message);
            }
        }
        await envoyerVocalPublic(blob, durSec);
    }

    async function envoyerVocalPublic(blob, durSec) {
        if (blob.size > 10 * 1024 * 1024) return popToast(t('toast.file_too_large'), 'error');
        const file = new File([blob], 'vocal.' + extFor(mimeType), { type: blob.type || mimeType });
        const fd = new FormData();
        fd.append('chatFile', file);
        fd.append('auteur', state.monPseudo);
        fd.append('salonId', state.salonActuelID);
        fd.append('destinataire', state.destinataireID || 'public');
        fd.append('voiceDuration', String(Math.round(durSec)));
        try {
            popToast(t('voicemsg.sending'), 'stream');
            const res = await fetch('/api/upload-chat-file', { method: 'POST', body: fd });
            if (!res.ok) { popToast(t('voicemsg.send_error'), 'error'); return; }
            const data = await res.json();
            if (data.success) popToast(t('voicemsg.sent'), 'success');
            else popToast(t('voicemsg.send_error'), 'error');
        } catch (err) {
            console.error('[Vocal] Upload public échoué :', err);
            popToast(t('voicemsg.send_error'), 'error');
        }
    }

    async function envoyerVocalE2EE(blob, durSec) {
        try {
            popToast(t('voicemsg.sending'), 'stream');
            const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
            const iv  = crypto.getRandomValues(new Uint8Array(12));
            const clair = await blob.arrayBuffer();
            const ciffre = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, clair);
            const rawKey = await crypto.subtle.exportKey('raw', key);

            const fd = new FormData();
            fd.append('blob', new Blob([ciffre], { type: 'application/octet-stream' }), 'v.enc');
            const res = await fetch('/api/upload-encrypted-blob', { method: 'POST', body: fd });
            if (!res.ok) { popToast(t('voicemsg.send_error'), 'error'); return; }
            const up = await res.json();
            if (!up.success) { popToast(t('voicemsg.send_error'), 'error'); return; }

            const descripteur = JSON.stringify({
                chaosVoice: 1, e2ee: 1,
                url: up.url,
                mime: blob.type || mimeType,
                dur: Math.round(durSec),
                key: abToB64(rawKey),
                iv: abToB64(iv.buffer)
            });
            // On utilise la fonction globale accrochée dans app.js
            const r = await window.envoyerMPE2EE(descripteur);
            if (r === 'sent') popToast(t('voicemsg.sent'), 'success');
            else if (r === 'fallback') popToast(t('voicemsg.send_error'), 'error');
        } catch (err) {
            console.error('[Vocal] Chiffrement/Upload E2EE échoué :', err);
            popToast(t('voicemsg.send_error'), 'error');
        }
    }
}